/**
 * ISO 8601 week date utilities for Wochenplaner.
 *
 * All week calculations use the Thursday-anchor rule (ISO 8601):
 * the week containing the first Thursday of the year is week 1,
 * and weeks start on Monday.
 */

/**
 * Returns the ISO 8601 week number for today's date.
 *
 * @returns {number} The current week number (1–53).
 */
export function getCurrentWeek() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  const y0 = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - y0) / 86400000 + 1) / 7);
}

/**
 * Returns an array of five Date objects representing Monday–Friday of the
 * given ISO week number in the current calendar year.
 *
 * @param {number} weekNumber - ISO week number (1–53).
 * @returns {Date[]} Five Date objects for Mon, Tue, Wed, Thu, Fri.
 */
export function getWeekDates(weekNumber) {
  const year = new Date().getFullYear();
  const janFirst = new Date(year, 0, 1);
  const janFirstDay = janFirst.getDay() || 7;
  const monday = new Date(year, 0, 2 - janFirstDay + (weekNumber - 1) * 7);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

/**
 * Returns the ISO 8601 week number for the given date string.
 *
 * @param {string} dateString - A date in `YYYY-MM-DD` format.
 * @returns {number} The ISO week number (1–53) that contains that date.
 */
export function getWeekNumberForDate(dateString) {
  const date = new Date(dateString + 'T12:00:00');
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
}

/**
 * Formats a Date object as an ISO 8601 date string (`YYYY-MM-DD`).
 * Uses the Swedish locale as a reliable way to get the ISO date format.
 *
 * @param {Date} d - The date to format.
 * @returns {string} Date string in `YYYY-MM-DD` format.
 */
export function toISO(d) {
  return d.toLocaleDateString('sv');
}

/**
 * Formats a Date object as a short Swiss date string (`dd.mm.`).
 * Used for day-pill labels, week-range displays, and table cells.
 *
 * @param {Date} d - The date to format.
 * @returns {string} Date string in `dd.mm.` format per de-CH locale.
 */
export function formatDate(d) {
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
}
